/**
 * Module Permissions Configuration
 *
 * Each module requires at least one of the specified permissions (OR).
 * Resource keys align with API `user_companies_and_roles` payload (see example.json).
 */

export interface ModulePermission {
  resource: string;
  action: string;
}

export interface ModulePermissionsConfig {
  [moduleName: string]: ModulePermission[];
}

export const MODULE_PERMISSIONS: ModulePermissionsConfig = {
  users: [
    { resource: 'users', action: 'read' },
    { resource: 'roles', action: 'read' },
  ],

  roles: [
    { resource: 'roles', action: 'read' },
    { resource: 'users', action: 'read' },
  ],

  employees: [
    { resource: 'employees', action: 'read' },
    { resource: 'departments', action: 'read' },
  ],

  orders: [
    { resource: 'orders', action: 'read' },
    { resource: 'orders', action: 'view_others_order' },
  ],

  'order-progress': [{ resource: 'orders.orderprogress', action: 'read' }],

  transactions: [
    { resource: 'transaction', action: 'read' },
    { resource: 'transaction', action: 'view_all_transactions' },
    { resource: 'transaction', action: 'view_income_only' },
    { resource: 'transaction', action: 'view_expense_only' },
  ],

  'human-resources': [
    { resource: 'employees', action: 'read' },
    { resource: 'departments', action: 'read' },
  ],

  finance: [
    { resource: 'transaction', action: 'read' },
    { resource: 'transaction', action: 'view_all_transactions' },
    { resource: 'transaction', action: 'view_income_only' },
    { resource: 'transaction', action: 'view_expense_only' },
    { resource: 'transaction', action: 'approve_transaction' },
    { resource: 'transactiontype', action: 'read' },
  ],

  'financial-report': [
    { resource: 'transaction', action: 'read' },
    { resource: 'transaction', action: 'view_all_transactions' },
    { resource: 'transaction', action: 'view_income_only' },
    { resource: 'transaction', action: 'view_expense_only' },
  ],

  sales: [
    { resource: 'orders', action: 'read' },
    { resource: 'orders', action: 'view_others_order' },
    { resource: 'orders', action: 'print' },
    { resource: 'orders', action: 'export' },
    { resource: 'customer', action: 'read' },
  ],

  purchase: [
    { resource: 'orders', action: 'read' },
    { resource: 'supplier', action: 'read' },
    { resource: 'inventory.purchase', action: 'read' },
  ],

  inventory: [
    { resource: 'inventory.item', action: 'read' },
    { resource: 'inventory.stockbalance', action: 'read' },
  ],

  allocations: [
    { resource: 'inventory.materialrequest', action: 'read' },
  ],

  'request-material': [{ resource: 'inventory.materialrequest', action: 'read' }],

  items: [
    { resource: 'inventory.item', action: 'read' },
  ],

  manufacturing: [
    { resource: 'productionorder', action: 'read' },
  ],

  project: [
    { resource: 'tasks.task', action: 'read' },
  ],

  crm: [
    { resource: 'customer', action: 'read' },
  ],

  communication: [
    { resource: 'email', action: 'read' },
  ],

  tools: [
    { resource: 'tools', action: 'read' },
  ],

  quality: [
    { resource: 'issue', action: 'read' },
  ],

  settings: [
    { resource: 'settings.printconfig', action: 'read' },
    { resource: 'settings.statusmanage', action: 'read' },
    { resource: 'settings.producttypetaxconfig', action: 'read' },
  ],

  timekeepings: [
    { resource: 'timekeepings', action: 'read' },
    { resource: 'timesheets', action: '*' },
  ],

  reports: [
    { resource: 'reports', action: 'view_sales_report' },
    { resource: 'reports', action: 'view_delivery_report' },
    { resource: 'reports', action: 'view_income_expense_report' },
  ],
};

export const getModulePermissions = (moduleName: string): ModulePermission[] => {
  return MODULE_PERMISSIONS[moduleName] || [];
};

export const hasModulePermissions = (moduleName: string): boolean => {
  return MODULE_PERMISSIONS[moduleName] !== undefined;
};

export const getAvailableModules = (): string[] => {
  return Object.keys(MODULE_PERMISSIONS);
};

